import React from "react";
import { Card, Spin } from "antd";
import { formatDate } from "../utils/workbenchNewSubjectView";
import {
    CaseSummaryValue,
    CaseSummaryReferenceValue,
    WbCaseSummary,
    WbSubject,
} from "../../workbench/types/WbTypes";

type Props = {
    broker: string;
    caseSummary?: WbCaseSummary | null;
    cedent: string;
    claimant: string;
    error?: string | null;
    isLoading?: boolean;
    mainClass: string;
    subject?: WbSubject | null;
};

const emptyValue = "Not available";
const sessionLink = (summary?: WbCaseSummary | null) => {
    if (!summary?.resultUrl || !summary.aiSessionId) return undefined;
    try {
        const url = new URL(summary.resultUrl);
        if (
            !["http:", "https:"].includes(url.protocol) ||
            url.username ||
            url.password ||
            url.hash ||
            !/^[A-Za-z0-9_-]+$/.test(summary.aiSessionId) ||
            !url.pathname.endsWith(`/sessions/${summary.aiSessionId}`) ||
            (url.search !== "" && url.search !== "?toolbar=0")
        )
            return undefined;
        return url.href;
    } catch {
        return undefined;
    }
};
const display = (value?: CaseSummaryValue) =>
    value?.state && value.state !== "Missing"
        ? value.text || emptyValue
        : emptyValue;

const referenceText = (reference?: CaseSummaryReferenceValue | null) =>
    reference ? reference.value?.trim() || emptyValue : undefined;

const textValue = (
    label: string,
    values: Array<string | null | undefined>,
    separator: string,
): CaseSummaryValue => {
    const unique = values
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
        .filter(
            (value, index, all) =>
                all.findIndex(
                    (other) => other.toLowerCase() === value.toLowerCase(),
                ) === index,
        );
    return {
        label,
        state: unique.length ? "Available" : "Missing",
        text: unique.join(separator),
    };
};

const classValue = (summary?: WbCaseSummary | null): CaseSummaryValue => {
    const seen = new Set<string>();
    const names = (references?: CaseSummaryReferenceValue[] | null) =>
        (references ?? [])
            .map(referenceText)
            .filter((value): value is string => Boolean(value))
            .filter((value) => {
                const key = value.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .join(", ");
    return textValue(
        "Class of Business",
        [
            names(summary?.mainClass),
            names(summary?.class),
            names(summary?.subClass),
        ],
        " → ",
    );
};

const structure = (summary?: WbCaseSummary | null) => {
    if (!summary || summary.source === "Subject") return emptyValue;
    const layers = summary.layerCount ?? 0;
    const sections = summary.sectionCount ?? 0;
    if (layers && sections) return `${layers} layers + sections`;
    if (layers) return `${layers} layers`;
    if (sections) return `${sections} sections`;
    return "No layers or sections";
};

const limitLabel = (summary?: WbCaseSummary | null) => {
    const saved = summary?.limit?.label?.trim();
    if (saved && saved.toLowerCase() !== "limit") return saved;
    if (summary?.proportionality === "Non-Proportional") return "Cover";
    if (
        summary?.proportionality === "Proportional" &&
        summary.placement === "Treaty"
    )
        return "Liability";
    if (
        summary?.proportionality === "Proportional" &&
        (summary.placement === "Direct" || summary.placement === "Facultative")
    )
        return "Main Limit";
    return undefined;
};

const limitText = (value?: CaseSummaryValue) => {
    const text = display(value);
    return value?.state === "ByScope" &&
        /^By (section|layer|layer\/section)$/i.test(text)
        ? `Varies by ${text.slice(3)}`
        : text;
};

const WorkbenchNewSubjectSummary: React.FC<Props> = ({
    caseSummary,
    error,
    isLoading = false,
}) => {
    const summary = caseSummary;
    const headlineStructure = structure(summary);
    const headlineLimit = limitText(summary?.limit);
    const headlinePremium = display(summary?.premium);
    const headlineLimitLabel =
        headlineLimit === emptyValue ? "Limit" : limitLabel(summary);
    const savedPremiumLabel = summary?.premium?.label?.trim();
    const headlinePremiumLabel =
        headlinePremium === emptyValue
            ? "Premium"
            : savedPremiumLabel?.toLowerCase() !== "premium"
              ? savedPremiumLabel
              : undefined;
    const profile = [
        summary?.placement,
        summary?.proportionality,
        referenceText(summary?.participation),
    ]
        .filter(Boolean)
        .join(" · ");
    const party =
        summary?.placement === "Direct" || summary?.placement === "Facultative"
            ? summary.insured
            : summary?.placement === "Treaty" || summary?.source === "Subject"
              ? summary?.broker?.state && summary.broker.state !== "Missing"
                  ? summary.broker
                  : summary.cedent
              : summary?.cedent;
    const facts: Array<[string, CaseSummaryValue | undefined]> = [
        ["Coverage Period", summary?.period],
        ["Class of Business", classValue(summary)],
        [
            "Territory",
            textValue(
                "Territory",
                [
                    ...(summary?.countries ?? []),
                    ...(summary?.countryGroups ?? []),
                ].map(referenceText),
                ", ",
            ),
        ],
        [party?.label || "Relevant Party", party],
        [summary?.share?.label || "Share", summary?.share],
        [summary?.keyTerm?.label || "Key Term", summary?.keyTerm],
    ];
    const hasAnalysis =
        summary?.source === "AlphaSnapshot" || summary?.source === "SICS";
    const isSics = summary?.source === "SICS";
    const footerDate = isSics
        ? summary.capturedAt
        : (summary?.alphaAnalyzedAt ??
          (summary?.source === "AlphaSnapshot"
              ? summary.capturedAt
              : undefined));
    const formattedFooterDate =
        footerDate && !Number.isNaN(new Date(footerDate).getTime())
            ? formatDate(footerDate)
            : emptyValue;
    const analysisLink = hasAnalysis ? sessionLink(summary) : undefined;

    return (
        <Card className="workbench-new-subject-card workbench-new-subject-summary">
            <div className="workbench-new-subject-summary-title-row">
                <div className="workbench-new-subject-card-title">
                    Case summary
                </div>
                {hasAnalysis && (
                    <span
                        className={`workbench-new-entity-type ${isSics ? "workbench-new-sics-badge" : "workbench-new-analysis-badge"}`}
                    >
                        {isSics ? "SICS data" : "Alpha Intelligence analysis"}
                    </span>
                )}
            </div>
            <div
                className="workbench-new-subject-ai-summary"
                aria-label="Contract Profile"
            >
                <span
                    className="workbench-new-subject-ai-summary-copy"
                    title={profile}
                >
                    {isLoading ? <Spin size="small" /> : profile || emptyValue}
                </span>
            </div>
            {error && (
                <div className="workbench-new-subject-inline-error">
                    {error}
                </div>
            )}
            <div className="workbench-new-subject-metrics">
                <div className="workbench-new-subject-metric workbench-new-subject-metric-structure workbench-new-subject-metric-label-first">
                    <span>Structure</span>
                    <strong>{headlineStructure}</strong>
                </div>
                <div className="workbench-new-subject-metric workbench-new-subject-metric-limit workbench-new-subject-metric-label-first">
                    {headlineLimitLabel && <span>{headlineLimitLabel}</span>}
                    <strong>{headlineLimit}</strong>
                </div>
                <div className="workbench-new-subject-metric workbench-new-subject-metric-premium workbench-new-subject-metric-label-first">
                    {headlinePremiumLabel && (
                        <span>{headlinePremiumLabel}</span>
                    )}
                    <strong>{headlinePremium}</strong>
                </div>
            </div>
            <div className="workbench-new-subject-summary-subtitle">
                Key contract facts
            </div>
            <dl className="workbench-new-subject-facts">
                {facts.map(([label, value], index) => (
                    <div
                        key={index}
                        className={
                            index === 1
                                ? "workbench-new-subject-class-fact"
                                : undefined
                        }
                    >
                        <dt>{label}</dt>
                        <dd title={display(value)}>{display(value)}</dd>
                    </div>
                ))}
            </dl>
            <div className="workbench-new-subject-case-footer">
                <div className="workbench-new-subject-data-state">
                    <span className="workbench-new-subject-data-source">
                        <span>Source:</span>
                        <strong>
                            {caseSummary?.source === "SICS"
                                ? "SICS"
                                : caseSummary?.sourceFileName ||
                                  caseSummary?.source ||
                                  emptyValue}
                        </strong>
                    </span>
                    {caseSummary?.source === "SICS" && (
                        <span className="workbench-new-subject-data-detail">
                            <span className="workbench-new-subject-data-separator">
                                |
                            </span>
                            <span>
                                {caseSummary.sicsContractIds &&
                                caseSummary.sicsContractIds.length > 1
                                    ? "Contracts: "
                                    : "Contract: "}
                                <strong>
                                    {caseSummary.sicsContractIds?.join(", ") ||
                                        caseSummary.sicsContractId ||
                                        emptyValue}
                                </strong>
                            </span>
                        </span>
                    )}
                    {(hasAnalysis || summary?.source === "Subject") && (
                        <span className="workbench-new-subject-data-detail">
                            <span className="workbench-new-subject-data-separator">
                                |
                            </span>
                            <span>
                                {isSics ? "Captured" : "Alpha analysis"}:{" "}
                                <strong>
                                    {hasAnalysis
                                        ? formattedFooterDate
                                        : emptyValue}
                                </strong>
                            </span>
                        </span>
                    )}
                    <span className="workbench-new-subject-data-detail">
                        <span className="workbench-new-subject-data-separator">
                            |
                        </span>
                        <span>
                            SICS:{" "}
                            <strong>
                                {caseSummary?.sicsStatus || emptyValue}
                            </strong>
                        </span>
                    </span>
                </div>
                {analysisLink && (
                    <a
                        className="workbench-new-programme-link"
                        href={analysisLink}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        Open in Alpha Intelligence
                    </a>
                )}
            </div>
        </Card>
    );
};

export default WorkbenchNewSubjectSummary;
